import React, { useRef } from 'react';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { FaPrint, FaReceipt } from 'react-icons/fa';
import { useQZTray } from '../../context/QZTrayContext';
import { useTranslation } from '../../context/LanguageContext';
import createCategoryReportThermal from './thermal-templates/CategoryReportThermal';
import qz from 'qz-tray';

// Print-specific styles
const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        .category-report,
        .category-report * {
            visibility: visible;
        }
        .category-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
        }
        .no-print {
            display: none !important;
        }
    }
`;

const CategoryReport = ({ data, language, settings }) => {
    // eslint-disable-next-line no-unused-vars
    const { t } = useTranslation();
    const { isConnected, connectQZ, printers } = useQZTray();
    // eslint-disable-next-line no-unused-vars
    const printerRef = useRef(null);

    if (!data?.categories || !data?.summary) return null;

    // Ensure returns data exists
    const { categories, summary } = data;
    const returns = data.returns || { total_amount: 0, count: 0, percentage: 0 };
    
    // Filter out categories with no sales
    const activeCategories = categories.filter(cat => cat.total_revenue > 0);
    
    // Process return orders data if available
    const hasReturns = returns && (returns.count > 0 || returns.total_amount > 0);

    const handlePrint = () => {
        window.print();
    };

    // Display the reporting timeframe

    const handleThermalPrint = async () => {
        if (!settings || !settings.printing || !settings.printing.printer_name) {
            console.error('Printer not configured in settings');
            return;
        }

        if (!isConnected) {
            try {
                await connectQZ();
            } catch (err) {
                console.error('Failed to connect to printer:', err);
                return;
            }
        }

        // Debug returns data before thermal printing

        
        // Get the date range from the query parameters if available
        const urlParams = new URLSearchParams(window.location.search);
        const startDate = urlParams.get('start_date') || '';
        const endDate = urlParams.get('end_date') || '';
        
        // Add date range to data object
        const dataWithDateRange = {
            ...data,
            date_range: {
                start: startDate,
                end: endDate
            }
        };

        const thermalContent = createCategoryReportThermal(dataWithDateRange, language, settings);

        const { printing } = settings;
        const paperWidth = printing.paper_size || '80mm';
        const paperHeight = '210mm';
        // eslint-disable-next-line no-unused-vars
        const fontSize = printing.font_size || '10';

        // Calculate dimensions for printer config
        let pageWidth = parseInt(paperWidth);
        if (isNaN(pageWidth)) {
            console.error('Invalid paper width:', paperWidth);
            pageWidth = 80; // Use 80mm as default width
        }
        
        // Parse height properly - critical for preventing infinite printing
        let pageHeight;
        if (paperHeight === 'auto') {
            pageHeight = 9999; // Greatly increased default height to accommodate larger fonts

        } else {
            pageHeight = parseInt(paperHeight.replace('mm', ''));
            if (isNaN(pageHeight)) {
                console.error('Invalid paper height:', paperHeight);
                pageHeight = 9999; // Greatly increased default height
            }
        }


        // Find the matching printer
        const matchingPrinter = printers.find(printer => 
            printer.value === printing.printer_name || 
            printer.label === printing.printer_name
        );

        if (!matchingPrinter) {
            console.error(`Selected printer "${printing.printer_name}" not found in available printers:`, printers);
            return;
        }

        // Create base printer config
        const config = qz.configs.create(matchingPrinter.value);
        
        // Asymmetric margin configuration to balance the output
        config.reconfigure({
            size: { width: pageWidth, height: pageHeight },
            margins: { top: 0, right: 4, bottom: 0, left: 0}, // More right margin to balance
            colorType: 'grayscale',
            orientation: 'portrait',
            units: 'mm',
            density: '203dpi', // Typical thermal printer density
            scaleContent: false // Don't scale content - preserve font sizes
        });

        // Create simple print data with minimal options
        const printData = [{
            type: 'html',
            format: 'plain',
            data: thermalContent,
            options: {
                pageWidth: `${pageWidth-10}mm`, // Narrower with explicit margins
                metaContent: `<meta name="align" content="center">`, // Force center alignment
                css: true
            }
        }];

        // Print the report
        qz.print(config, printData)
            .then(() => {
    
            })
            .catch(err => {
                console.error('[CategoryReport] Print failed:', err);
            });
    };

    return (
        <div className="category-report mt-8 space-y-8" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <style>{printStyles}</style>
            {/* Date Range Indicator */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-blue-700">
                            {language === 'ar' ? 'نطاق التقرير:' : 'Report Range:'} 
                            <span className="font-medium ml-1">
                                {(() => {
                                    try {
                                        // Try to extract and format dates from the raw data
                                        if (data && data._meta && data._meta.date_range) {
                                            const { start_date, end_date } = data._meta.date_range;
                                            // Parse dates from ISO strings without timezone handling
                                            const startDate = new Date(start_date);
                                            const endDate = new Date(end_date);
                                            
                                            // Simple date format without timezone information
                                            const dateTimeFormat = new Intl.DateTimeFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                calendar: 'gregory' // Force Gregorian calendar
                                            });
                                            
                                            // Format dates without timezone info
                                            return `${dateTimeFormat.format(startDate)} - ${dateTimeFormat.format(endDate)}`;
                                        }
                                        return language === 'ar' ? 'النطاق الزمني المحدد' : 'Selected Time Range';
                                    } catch (e) {
                                        return language === 'ar' ? 'النطاق الزمني المحدد' : 'Selected Time Range';
                                    }
                                })()}
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Print Button */}
            <div className="flex justify-end space-x-4 no-print" style={{ direction: 'ltr' }}>
                <button
                    onClick={handleThermalPrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                    <FaReceipt className="mr-2" />
                    {language === 'ar' ? 'طباعة حرارية' : 'Thermal Print'}
                </button>
                <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <FaPrint className="mr-2" />
                    {language === 'ar' ? 'طباعة التقرير' : 'Print Report'}
                </button>
            </div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'إجمالي المبيعات' : "Total Revenue"}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatCurrency(summary.total_revenue)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'عدد الطلبات' : "Total Orders"}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatNumber(summary.total_orders)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'المنتجات المباعة' : "Total Items Sold"}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatNumber(summary.total_items_sold)}
                    </p>
                </div>
            </div>

            {/* Return Orders Summary (if available) */}
            {hasReturns && (
                <div className="bg-red-50 rounded-lg p-6 border border-red-100">
                    <h3 className="text-lg font-semibold mb-3 text-red-700">
                        {language === 'ar' ? 'ملخص المرتجعات' : 'Returns Summary'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'إجمالي المرتجعات' : "Total Returns Amount"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatCurrency(returns.total_amount || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'عدد الطلبات المرتجعة' : "Number of Returns"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatNumber(returns.count || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'نسبة المرتجعات' : "Returns Percentage"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {returns.percentage ? `${returns.percentage.toFixed(2)}%` : '0%'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {/* Categories List */}
            <div className="grid grid-cols-1 gap-6">
                {activeCategories.map((category) => (
                    <div key={category.id} className="bg-white rounded-lg shadow p-6">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">
                                    {language === 'ar' ? category.name_ar : category.name}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {category.revenue_share.toFixed(1)}% {language === 'ar' ? 'من المبيعات' : 'of sales'}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">
                                    {formatCurrency(category.total_revenue)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    {formatNumber(category.total_quantity)} {language === 'ar' ? 'قطعة' : 'items'}
                                </p>
                            </div>
                        </div>



                        {/* Revenue Progress Bar */}
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
                            <div
                                className="h-full bg-indigo-600 rounded-full"
                                style={{ width: `${category.revenue_share}%` }}
                            />
                        </div>

                        {/* Items List */}
                        {category.items && category.items.length > 0 && (
                            <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                    {language === 'ar' ? 'المنتجات المباعة' : 'Items Sold'}
                                </h4>
                                <div className="space-y-2">
                                    {category.items.map((item) => (
                                        <div key={item.id} className="flex justify-between items-center text-sm">
                                            <span className="text-gray-600">
                                                {language === 'ar' ? item.name_ar : item.name}
                                            </span>
                                            <div className="text-right">
                                                <p className="font-medium">{formatCurrency(item.revenue)}</p>
                                                <p className="text-gray-500">{formatNumber(item.quantity)} {language === 'ar' ? 'قطعة' : 'items'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}


                    </div>
                ))}
            </div>
        </div>
    );
};

export default CategoryReport;
